import React, { useContext, useEffect, useState, type ChangeEvent, type SetStateAction } from 'react';

import isString from 'lodash/isString';
import debounce from 'lodash/debounce';
import { useSmartlingStrings, useURLParams } from '@lucid/hooks';

import ChildCategorySection from './child-category-section';
import AccessibleDescription from '../accessible-description/accessible-description';
import Box from '../box/box';
import Container from '../container/container';
import LayoutContext from '../context/layout-context';
import SearchBar from '../input/search-bar/search-bar';
import type { Lucid } from '../../types/types';

interface TemplateHubSearchProps {
  onSearch?: (value: SetStateAction<boolean>) => void;
  categoryType?: string;
  shouldRenderResults?: boolean;
  allProductTemplates: Lucid.Template.ProductTemplate.Data[];
}

const TemplateHubSearch: React.FC<TemplateHubSearchProps> = ({
  onSearch,
  shouldRenderResults,
  categoryType,
  allProductTemplates,
}) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_ct_cs_search_description',
      'mktg_template_search_placeholder',
      'mktg_generics_search_result_count',
      'mktg_generics_search_result_count_other',
      'mktg_search_no_results',
    ],
    shortCode,
  });
  const [currentTemplates, setCurrentTemplates] = useState<Lucid.Template.ProductTemplate.Data[]>([]);
  const [search, setSearch] = useState('');
  const { paramValue: sParamValue, updateURLParam: updateSURLParam } = useURLParams('s');
  const { paramValue: pParamValue, updateURLParam: updatePURLParam } = useURLParams('p');

  const handleSearch = debounce((value: string) => {
    setSearch(value.toLowerCase());
    updateSURLParam(value.toLowerCase());

    if (value === '') {
      updatePURLParam('');
    } else {
      updatePURLParam('1');
    }
  }, 500);

  useEffect(() => {
    onSearch && onSearch(!!search);

    if (search) {
      type WeightedTemplate = Lucid.Template.ProductTemplate.Data & { weight: number };
      const filteredTemplates = allProductTemplates
        .reduce((prev, node) => {
          const [searchTags] = node.CustomDocumentAttribute || [{ value: "['']" }];
          let parseSearchTags: string[] = [];

          if (isString(searchTags?.value)) {
            parseSearchTags = JSON.parse(searchTags.value);
          }

          let weight = 0;

          const title = node.Document.title.toLowerCase();
          const metaTitle = node.metadata?.metaTitle?.toLowerCase() || '';
          const allSearchTags = parseSearchTags.map(tag => tag.toLowerCase()).filter(Boolean);
          const category = [
            ...(node.ParentCategories?.map(category => category.name.toLowerCase()) || []),
            ...(node.AdditionalCategories?.map(category => category.name.toLowerCase()) || []),
          ].join(' ');

          if (allSearchTags.includes(search)) weight++;
          if (category.includes(search)) weight += 2;
          if (title.includes(search)) weight += 3;
          if (metaTitle.includes(search)) weight += 4;

          if (!weight) return prev;

          return [...prev, { ...node, weight }];
        }, [] as WeightedTemplate[])
        .sort((a, b) => b.weight - a.weight);

      return setCurrentTemplates(filteredTemplates);
    }

    return setCurrentTemplates([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (sParamValue) {
      setSearch(sParamValue);
    }
  }, [sParamValue, pParamValue]);

  return (
    <>
      <Container
        as="section"
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
        }}
        extraPadding={3}
      >
        <Box css={theme => ({ width: '100%', maxWidth: '980px', paddingBottom: theme.space.s64 })}>
          <AccessibleDescription id="nav-search-description" SRText={withSmartling('mktg_ct_cs_search_description')} />
          <SearchBar
            aria-describedby="nav-search-description"
            defaultValue={sParamValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
            placeholder={withSmartling('mktg_template_search_placeholder')}
          />
        </Box>
      </Container>
      {shouldRenderResults && (
        <ChildCategorySection
          categories={currentTemplates}
          categoryType={categoryType}
          heading={withSmartling('mktg_generics_search_result_count', { count: `${currentTemplates.length}` })}
          pageSize={15}
          pParam={pParamValue || '1'}
          setPageParam={updatePURLParam}
          sParam={sParamValue}
          startPage={1}
        />
      )}
    </>
  );
};

export default TemplateHubSearch;
